import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Link } from 'react-router-dom';

/**
 * Public landing page (route: "/").
 *
 * A polished, standalone landing screen for visitors: a hero with a Log In
 * call-to-action, a plain-language summary of what the app does, and an
 * ownership/footer note. Authenticated members never see this — they're sent
 * straight to the app. The app's banner, member navbar, and global footer are
 * hidden on this route (see App.tsx) so it reads as a true landing page.
 */

const FEATURES = [
  {
    icon: '⛳',
    title: 'Track your rounds',
    text: 'Post your scores after each round and watch your season come together.',
  },
  {
    icon: '🏆',
    title: 'Standings & majors',
    text: "See who's on top, follow the leaderboard, and battle for major titles.",
  },
  {
    icon: '📉',
    title: 'Automatic handicaps',
    text: 'Your handicap updates on its own as you log scores — no math required.',
  },
  {
    icon: '📅',
    title: 'Tee times',
    text: "Keep all of the group's tee-time bookings organized in one tidy place.",
  },
];

const Home: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  // Members skip the landing and go straight to the app.
  if (isAuthenticated) {
    return <Navigate to="/rounds" replace />;
  }

  return (
    <div style={pageStyle}>
      {/* ── Hero ── */}
      <section style={hero}>
        <div style={heroInner}>
          <p style={eyebrow}>Broskies Golf Association</p>
          <h1 style={headline}>Your golf league, all in one place.</h1>
          <p style={subhead}>
            Track your rounds, follow the standings, and stay connected with the
            group — everything our league does, in one simple app.
          </p>

          <button style={loginBtn} onClick={() => loginWithRedirect()}>
            Log In
          </button>

          <p style={createLine}>
            New to the league?{' '}
            {/* Account creation isn't built yet — link intentionally goes nowhere for now. */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" onClick={(e) => e.preventDefault()} style={createLink}>
              Create an account
            </a>
          </p>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={featuresSection}>
        <h2 style={featuresHeading}>What you can do</h2>
        <div style={grid}>
          {FEATURES.map((f) => (
            <div key={f.title} style={card}>
              <span style={iconCircle} aria-hidden="true">
                {f.icon}
              </span>
              <h3 style={cardTitle}>{f.title}</h3>
              <p style={cardText}>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={footer}>
        <p style={ownership}>
          This website and web application are owned and operated by the{' '}
          <strong>Broskies Golf Association</strong>.
        </p>
        <p style={footerLinks}>
          <Link to="/privacy" style={footerLink}>
            Privacy Policy
          </Link>
          <span style={{ color: '#ccc', margin: '0 8px' }}>·</span>
          <span>© {new Date().getFullYear()} Broskies Golf Association</span>
        </p>
      </footer>
    </div>
  );
};

// ── Styles ──
const pageStyle: React.CSSProperties = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  color: '#1c2b22',
};

const hero: React.CSSProperties = {
  background:
    'linear-gradient(135deg, #0d4429 0%, #1a7d4b 55%, #34a766 100%)',
  color: '#fff',
  textAlign: 'center',
  padding: '84px 24px 92px',
};
const heroInner: React.CSSProperties = { maxWidth: 640, margin: '0 auto' };
const eyebrow: React.CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  fontSize: 13,
  fontWeight: 600,
  color: '#f4d58d',
  margin: '0 0 18px',
};
const headline: React.CSSProperties = {
  fontSize: 'clamp(30px, 6vw, 46px)',
  lineHeight: 1.12,
  fontWeight: 800,
  margin: '0 0 18px',
};
const subhead: React.CSSProperties = {
  fontSize: 'clamp(16px, 2.4vw, 19px)',
  lineHeight: 1.6,
  color: 'rgba(255,255,255,0.9)',
  margin: '0 auto 34px',
  maxWidth: 520,
};
const loginBtn: React.CSSProperties = {
  padding: '14px 46px',
  background: '#fff',
  color: '#0d4429',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  fontSize: 17,
  fontWeight: 700,
  boxShadow: '0 8px 22px rgba(0,0,0,0.18)',
};
const createLine: React.CSSProperties = {
  marginTop: 20,
  fontSize: 15,
  color: 'rgba(255,255,255,0.85)',
};
const createLink: React.CSSProperties = {
  color: '#fff',
  fontWeight: 700,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const featuresSection: React.CSSProperties = {
  maxWidth: 980,
  margin: '0 auto',
  padding: '64px 24px 24px',
};
const featuresHeading: React.CSSProperties = {
  textAlign: 'center',
  fontSize: 26,
  fontWeight: 700,
  margin: '0 0 36px',
};
const grid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: 20,
};
const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e8ece9',
  borderRadius: 14,
  padding: '26px 22px',
  textAlign: 'center',
  boxShadow: '0 4px 14px rgba(13,68,41,0.05)',
};
const iconCircle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: '#eaf6ef',
  fontSize: 26,
  marginBottom: 14,
};
const cardTitle: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 700,
  margin: '0 0 8px',
};
const cardText: React.CSSProperties = {
  fontSize: 14.5,
  lineHeight: 1.55,
  color: '#5a6b62',
  margin: 0,
};

const footer: React.CSSProperties = {
  textAlign: 'center',
  padding: '48px 24px 56px',
  color: '#6b7a72',
};
const ownership: React.CSSProperties = {
  fontSize: 14,
  margin: '0 0 10px',
};
const footerLinks: React.CSSProperties = {
  fontSize: 13,
  color: '#8a978f',
  margin: 0,
};
const footerLink: React.CSSProperties = {
  color: '#1a7d4b',
  fontWeight: 600,
  textDecoration: 'none',
};

export default Home;
